import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import HomepageSlidersApi from 'libs/api/website/homepageSliders';
import AuthService from 'services/Auth';

const useHomepageSliders = () => {
  const [sliders, setSliders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({});
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [shoppingCenters, setShoppingCenters] = useState([]);
  
  const navigate = useNavigate();
  const paramsRef = useRef({});


  const checkAuth = useCallback(() => {
    const token = AuthService.getToken();
    if (!token) {
      message.error('You are not authorized. Please login.');
      navigate('/login');
      return false;
    }
    return true;
  }, [navigate]);

 
  const fetchSliders = useCallback(async (params = {}) => {
    if (!checkAuth()) return;

    setLoading(true);
    try {
      const response = await HomepageSlidersApi.list(params);
      
      if (response.data.success) {
        setSliders(response.data.homepage_slides || []);
        setTotal(response.data.pagination?.total_count || 0);
      } else {
        setSliders([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Error fetching sliders:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
      } else {
        message.error(error.response?.data?.message || 'Failed to load sliders');
      }
    } finally {
      setLoading(false);
    }
  }, [checkAuth, navigate]);

  const fetchCompanies = useCallback(async () => {
    if (!checkAuth()) return;

    setLoadingCompanies(true);
    try {
      const response = await HomepageSlidersApi.getCompanies();
      const companiesRaw = response.data.companies || [];
      const companies = companiesRaw.map(c => ({
        value: c.id || c.value,
        title: c.title || c.name || '',
        name: c.name || c.title || '',
        shopping_center: c.shopping_center || '',
        shopping_center_title: c.shopping_center_title || '',
      }));
      setCompanies(companies);
    } catch (error) {
      console.error('Error fetching companies:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
      } else {
        message.error('Failed to load companies');
      }
    } finally {
      setLoadingCompanies(false);
    }
  }, [checkAuth, navigate]);

  const fetchSlider = useCallback(async (id) => {
    if (!checkAuth()) return null;

    try {
      const response = await HomepageSlidersApi.show(id);
      if (response.data.success) {
        return response.data.homepage_slide;
      }
      return null;
    } catch (error) {
      console.error('Error fetching slider:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
      } else {
        message.error(error.response?.data?.message || 'Failed to load slider');
      }
      return null;
    }
  }, [checkAuth, navigate]);

  const createSlider = useCallback(async (data) => {
    if (!checkAuth()) return null;

    try {
      const response = await HomepageSlidersApi.create(data);
      if (response.data.success) {
        return response.data.success;
      }

      const error = new Error(
        response.data.message || 'Failed to create slider',
      );
      error.response = { data: response.data };
      throw error;
    } catch (error) {
      console.error('Error creating slider:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
        return null;
      }
      throw error;
    }
  }, [checkAuth, navigate]);

  const updateSlider = useCallback(async (id, data) => {
    if (!checkAuth()) return null;

    try {
      const response = await HomepageSlidersApi.update(id, data);
      if (response.data.success) {
        return response.data.homepage_slide;
      }

      const error = new Error(
        response.data.message || 'Failed to update slider',
      );
      error.response = { data: response.data };
      throw error;
    } catch (error) {
      console.error('Error updating slider:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
        return null;
      }
      throw error;
    }
  }, [checkAuth, navigate]);

  const deleteSlider = useCallback(async (id) => {
    if (!checkAuth()) return false;

    try {
      await HomepageSlidersApi.destroy(id);
      message.success('Slider deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting slider:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
      } else {
        message.error(error.response?.data?.message || 'Failed to delete slider');
      }
      return false;
    }
  }, [checkAuth, navigate]);

  const fetchFilters = useCallback(async () => {
    if (!checkAuth()) return;
    setLoadingCompanies(true);
    try {
      const response = await HomepageSlidersApi.getFilters(); 
      const companiesRaw = response.data.companies || [];
      const companies = companiesRaw.map(c => ({
        value: c.id || c.value,
        title: c.title || c.name || '',
        name: c.name || c.title || '',
        shopping_center: c.shopping_center || '',
        shopping_center_title: c.shopping_center_title || '',
      }));
      
      const shoppingCentersRaw = response.data.shopping_centers || [];
      setCompanies(companies);
      setShoppingCenters(shoppingCentersRaw);
    } catch (error) {
      console.error('Error fetching filters:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
      } else {
        message.error('Failed to load filters');
      }
    } finally {
      setLoadingCompanies(false);
    }
  }, [checkAuth, navigate]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);


  useEffect(() => {
    const params = {
      page: currentPage,
      per: pageSize,
    };
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params[key] = value;
      }
    });
    paramsRef.current = params;
    fetchSliders(params);
  }, [currentPage, pageSize, filters, fetchSliders]);


  const updateFilters = useCallback((newFilters) => {
    if (Object.keys(newFilters).length === 0) {
      setFilters({});
    } else {
      setFilters(prev => ({ ...prev, ...newFilters }));
    }
    setCurrentPage(1); 
  }, []);


  const handlePageChange = useCallback((page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  }, []);

  return {
    sliders,
    loading,
    total,
    currentPage,
    pageSize,
    filters,
    companies,
    shoppingCenters,
    loadingCompanies,
    updateFilters,
    handlePageChange,
    deleteSlider,
    createSlider,
    updateSlider,
    fetchSlider,
    refetch: () => fetchSliders(paramsRef.current),
  };
};

export default useHomepageSliders; 